import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTitleFromPath, ALL_ROUTES } from '@/Router';
import { ScrollToTop } from '../../ScrollToTop';
import { Navbar, type NavbarProps } from '../Navbar';
import { Sidebar, type SidebarMenuItem } from '../Sidebar';
import { Footer } from '../Footer/Footer';
import './app-layout.scss';

export interface AppLayoutProps {
  children: React.ReactNode;
  navbarProps?: Omit<NavbarProps, 'onMenuClick'>;
  navbarTitle?: string; // Dynamic title from child pages (for /atleti/:id, etc.)
  sidebarMenuItems?: SidebarMenuItem[];
  showNavbar?: boolean;
  className?: string;
}

/**
 * pro - Main layout wrapper with Navbar and Sidebar
 *
 * Features:
 * - Fixed navbar at top
 * - Slide-in sidebar with navigation
 * - Hamburger menu toggle
 * - Responsive design
 * - Maintains scroll position
 *
 * @param children - Main content
 * @param navbarProps - Props for Navbar component
 * @param sidebarMenuItems - Custom sidebar menu items
 * @param showNavbar - Whether to show the navbar (default: true)
 * @param className - Additional CSS classes
 */
export function AppLayout({
  children,
  navbarProps,
  navbarTitle,
  sidebarMenuItems,
  showNavbar = true,
  className = '',
}: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // Usa navbarTitle se fornito (da pagine dinamiche), altrimenti estrai e traduci dal percorso
  const pageTitle = useMemo(() => {
    if (navbarTitle) {
      return navbarTitle; // Titoli dinamici (da NavbarContext) sono già stringhe tradotte
    }

    const titleKey = getTitleFromPath(location.pathname);
    return titleKey ? t(titleKey) : ''; // Traduci la chiave i18n
  }, [navbarTitle, location.pathname, t]);

  // Detect if we're on HomePage to apply transparent background
  const isHomePage = location.pathname === ALL_ROUTES.basePath;

  // Pages where footer should be hidden
  const footerHiddenPages = [
    ALL_ROUTES.basePath,
    ALL_ROUTES.artworkDetail.replace('/:id', '')
  ];

  const isFooterToHide = footerHiddenPages.some( pathToHide =>  {
    if (pathToHide === ALL_ROUTES.basePath) {
      //se il percorso è della home deve essere esattamente uguale
      return pathToHide === location.pathname
    }

    return location.pathname.includes(pathToHide)
  })
  const showFooter = !isFooterToHide

  const handleMenuClick = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  return (
    <div className={`app-layout ${isHomePage ? 'app-layout--home-page' : ''} ${className}`}>
      {/* Reset scroll position on route change */}
      <ScrollToTop />

      {/* Navbar */}
      {showNavbar && (
        <Navbar
          {...navbarProps}
          title={pageTitle}
          onMenuClick={handleMenuClick}
          className={isHomePage ? 'navbar--transparent' : ''}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
        menuItems={sidebarMenuItems}
      />

      {/* Main content */}
      <main className={`app-layout__main ${showNavbar ? 'app-layout__main--with-navbar' : ''}`}>
        {children}
      </main>

      {/* Footer */}
      {showFooter && <Footer />}
    </div>
  );
}

export default AppLayout;
